/**
 * Per-user persistent ordering of the admin sidebar.
 *
 * Stores two things in localStorage (scoped by user id so multiple staff on
 * one machine don't collide):
 *   1. `mainOrder` — array of top-level item keys in user's preferred order
 *   2. `subOrders` — { [mainKey]: [...subKeys] } for each group's sub-tabs
 *
 * Any item missing from the persisted order falls back to its position in
 * the canonical config — so when we ADD new items in code, they appear at
 * the end of the user's existing order automatically (no reset required).
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_PREFIX = "glpflo-admin-nav-order:";

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId || "anon"}`;
}

function readStored(userId) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      mainOrder: Array.isArray(parsed.mainOrder) ? parsed.mainOrder : [],
      subOrders: parsed.subOrders && typeof parsed.subOrders === "object" ? parsed.subOrders : {},
    };
  } catch { return null; }
}

function writeStored(userId, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(value));
  } catch { /* quota / disabled */ }
}

/**
 * Apply a user-preferred order to a canonical list of items.
 * Items missing from `order` are appended in their canonical position.
 * Items in `order` that no longer exist are dropped.
 */
function applyOrder(canonical, order) {
  if (!Array.isArray(order) || order.length === 0) return canonical;
  const byKey = new Map(canonical.map((it) => [it.key, it]));
  const out = [];
  const seen = new Set();
  for (const k of order) {
    const item = byKey.get(k);
    if (item) { out.push(item); seen.add(k); }
  }
  for (const it of canonical) {
    if (!seen.has(it.key)) out.push(it);
  }
  return out;
}

export function useAdminNavOrder(userId, canonical) {
  const [order, setOrder] = useState(() => readStored(userId));
  // Sync if userId changes (e.g. account switch on same tab)
  useEffect(() => { setOrder(readStored(userId)); }, [userId]);

  const orderedMain = useMemo(
    () => applyOrder(canonical, order?.mainOrder),
    [canonical, order],
  );
  const orderedSubsFor = useCallback(
    (mainKey) => {
      const main = canonical.find((m) => m.key === mainKey);
      if (!main?.subs) return null;
      return applyOrder(main.subs, order?.subOrders?.[mainKey]);
    },
    [canonical, order],
  );

  const saveOrder = useCallback((next) => {
    setOrder(next);
    writeStored(userId, next);
  }, [userId]);

  const setMainOrder = useCallback((keys) => {
    const next = { mainOrder: keys, subOrders: order?.subOrders || {} };
    saveOrder(next);
  }, [order, saveOrder]);

  const setSubOrder = useCallback((mainKey, keys) => {
    const next = {
      mainOrder: order?.mainOrder || [],
      subOrders: { ...(order?.subOrders || {}), [mainKey]: keys },
    };
    saveOrder(next);
  }, [order, saveOrder]);

  const resetAll = useCallback(() => {
    setOrder(null);
    if (typeof window !== "undefined") {
      try { window.localStorage.removeItem(storageKey(userId)); } catch { /* ignore */ }
    }
  }, [userId]);

  return {
    orderedMain,
    orderedSubsFor,
    setMainOrder,
    setSubOrder,
    resetAll,
    isCustomized: !!(order?.mainOrder?.length || Object.keys(order?.subOrders || {}).length),
  };
}
